import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  InfoOutlined,
  FileCopy,
  CheckCircleOutline,
  PhotoCamera,
  DirectionsCar,
  Description,
  Badge,
  Edit,
  Save,
  Close,
  ExpandMore,
} from '@mui/icons-material';
import { PhotoSection, MediaSection } from 'types/images';

interface SectionConfig {
  icon: React.ComponentType<{
    fontSize?: 'small' | 'medium' | 'large' | 'inherit';
  }>;
  title: string;
  fields: string[];
}

interface MLResults {
  // Marbete fields
  marbeteNumber?: string;

  // Driver's license fields
  licenseNumber?: string;
  fullName?: string;
  dateOfBirth?: string;
  residentialAddress?: string;
  issueDate?: string;
  expirationDate?: string;
  issuingTerritory?: string;

  // Vehicle registration fields
  registrationNumber?: string;
  licensePlate?: string;
  titleNumber?: string;
  make?: string;
  model?: string;
  year?: string | number;
  vin?: string;
  color?: string;
  effectiveDate?: string;

  // License plate fields
  licensePlateNumber?: string;

  // VIN fields
  vinNumber?: string;

  // Vehicle angle fields
  angle?: string;

  // Metadata fields (will be filtered out)
  metadata?: Record<string, unknown>;
  detection?: Record<string, unknown>;
}

interface MLResultsDisplayProps {
  mlResults: string | MLResults;
  section: PhotoSection | MediaSection;
  claimId?: string;
  imageId?: string;
  onUpdate?: (field: string, value: string, editor: string) => Promise<void>;
}

const MLResultsDisplay: React.FC<MLResultsDisplayProps> = ({
  mlResults,
  section,
  onUpdate,
}) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [editedValues, setEditedValues] = useState<Record<string, string>>({});
  const [editingField, setEditingField] = useState<string | null>(null);
  const [tempValue, setTempValue] = useState<string>('');
  const [expanded, setExpanded] = useState<boolean>(true);

  if (!mlResults) {
    return null;
  }

  // Parse JSON string if needed
  const results: MLResults =
    typeof mlResults === 'string' ? JSON.parse(mlResults) : mlResults;

  // Format value based on field type
  const formatValue = (
    key: string,
    value: string | number | boolean,
  ): string => {
    if (value === null || value === undefined) {
      return 'N/A';
    }
    const stringValue = value.toString();

    return stringValue.toUpperCase();
  };

  // Get section specific icon and title
  const getSectionConfig = (): SectionConfig => {
    const configs: Record<string, SectionConfig> = {
      [PhotoSection.INSURED_MARBETE]: {
        icon: Description,
        title: 'Insurance Sticker',
        fields: ['marbeteNumber'],
      },
      [PhotoSection.INSURED_LICENSE]: {
        icon: Badge,
        title: "Driver's License",
        fields: [
          'licenseNumber',
          'fullName',
          'dateOfBirth',
          'residentialAddress',
          'issueDate',
          'expirationDate',
          'issuingTerritory',
        ],
      },
      [PhotoSection.INSURED_VEHICLE_LICENSE]: {
        icon: Description,
        title: 'Vehicle Registration',
        fields: [
          'fullName',
          'registrationNumber',
          'issueDate',
          'licensePlate',
          'titleNumber',
          'make',
          'model',
          'year',
          'color',
          'vin',
          'effectiveDate',
          'expirationDate',
          'marbeteNumber',
        ],
      },
      [PhotoSection.INSURED_TABLILLA]: {
        icon: DirectionsCar,
        title: 'License Plate',
        fields: ['licensePlateNumber'],
      },
      [PhotoSection.INSURED_VIN]: {
        icon: DirectionsCar,
        title: 'VIN',
        fields: ['vinNumber'],
      },
      [PhotoSection.INSURED_VEH_PHOTOS]: {
        icon: PhotoCamera,
        title: 'Vehicle Photo',
        fields: ['angle'],
      },
      [PhotoSection.INSURED_VEH_FRONTSIDE_PHOTO]: {
        icon: PhotoCamera,
        title: 'Front Side Photo',
        fields: ['angle'],
      },
      [PhotoSection.INSURED_VEH_REARSIDE_PHOTO]: {
        icon: PhotoCamera,
        title: 'Rear Side Photo',
        fields: ['angle'],
      },
      [PhotoSection.INSURED_VEH_LEFT_SIDE_PHOTO]: {
        icon: PhotoCamera,
        title: 'Left Side Photo',
        fields: ['angle'],
      },
      [PhotoSection.INSURED_VEH_RIGHT_SIDE_PHOTO]: {
        icon: PhotoCamera,
        title: 'Right Side Photo',
        fields: ['angle'],
      },
      [PhotoSection.INJURED_VEH_FRONTSIDE_PHOTO]: {
        icon: PhotoCamera,
        title: 'Front Side Photo',
        fields: ['angle'],
      },
      [PhotoSection.INJURED_VEH_REARSIDE_PHOTO]: {
        icon: PhotoCamera,
        title: 'Rear Side Photo',
        fields: ['angle'],
      },
      [PhotoSection.INJURED_VEH_LEFT_SIDE_PHOTO]: {
        icon: PhotoCamera,
        title: 'Left Side Photo',
        fields: ['angle'],
      },
      [PhotoSection.INJURED_VEH_RIGHT_SIDE_PHOTO]: {
        icon: PhotoCamera,
        title: 'Right Side Photo',
        fields: ['angle'],
      },
    };

    return (
      configs[section] || {
        icon: InfoOutlined,
        title: 'Information',
        fields: [],
      }
    );
  };

  // Handle copy to clipboard
  const handleCopy = async (value: string, field: string): Promise<void> => {
    await navigator.clipboard.writeText(value);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Handle starting edit mode
  const startEditing = (field: string, value: string) => {
    setEditingField(field);
    setTempValue(value);
  };

  // Handle saving edit
  const saveEdit = async (field: string) => {
    if (onUpdate) {
      await onUpdate(field, tempValue, 'Connect Agent in Helios Claims');
      setEditedValues({ ...editedValues, [field]: tempValue });
      setEditingField(null);
    }
  };

  // Handle canceling edit
  const cancelEdit = () => {
    setEditingField(null);
    setTempValue('');
  };

  const sectionConfig = getSectionConfig();
  const IconComponent = sectionConfig.icon;

  // Filter out metadata and only keep relevant fields
  const relevantData: Record<string, string | number | boolean> = {};
  sectionConfig.fields.forEach((field) => {
    if (results[field as keyof MLResults] !== undefined) {
      relevantData[field] = results[field as keyof MLResults] as
        | string
        | number
        | boolean;
    }
  });

  // If no relevant data, don't show anything
  if (Object.keys(relevantData).length === 0) {
    return null;
  }

  const shouldUseAccordion =
    section === PhotoSection.INSURED_LICENSE ||
    section === PhotoSection.INSURED_VEHICLE_LICENSE;

  const renderAccordionContent = () => (
    <Grid container spacing={0}>
      {Object.entries(relevantData).map(([key, value], index, array) => {
        const displayValue = editedValues[key] ?? formatValue(key, value);
        const isEditing = editingField === key;
        const isLastItem = index === array.length - 1;

        return (
          <Grid item xs={12} key={key}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                bgcolor: 'white',
                p: 1.5,
                borderBottom: isLastItem ? 'none' : '1px solid #e0e0e0',
                '&:hover': { bgcolor: '#f9fafb' },
              }}
            >
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontFamily: 'Connect', mb: 0.5 }}
                >
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </Typography>
                <Box sx={{ flex: 1 }}>
                  {isEditing ? (
                    <input
                      value={tempValue}
                      onChange={(e) => setTempValue(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '6px 8px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '4px',
                        fontFamily: 'Connect',
                        fontSize: '14px',
                        height: '32px',
                      }}
                    />
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'Connect',
                        minHeight: '32px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {displayValue}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box
                sx={{ display: 'flex', gap: 0.5, ml: 1, alignSelf: 'flex-end' }}
              >
                {isEditing ? (
                  <>
                    <IconButton
                      size="small"
                      onClick={() => saveEdit(key)}
                      sx={{
                        color: 'success.main',
                        width: 32,
                        height: 32,
                        '& svg': { fontSize: 18 },
                      }}
                    >
                      <Save />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={cancelEdit}
                      sx={{
                        color: 'error.main',
                        width: 32,
                        height: 32,
                        '& svg': { fontSize: 18 },
                      }}
                    >
                      <Close />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <IconButton
                      size="small"
                      onClick={() => startEditing(key, displayValue)}
                      sx={{
                        width: 32,
                        height: 32,
                        '& svg': { fontSize: 18 },
                      }}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(displayValue, key)}
                      sx={{
                        width: 32,
                        height: 32,
                        '& svg': { fontSize: 18 },
                      }}
                    >
                      {copiedField === key ? (
                        <CheckCircleOutline fontSize="small" color="success" />
                      ) : (
                        <FileCopy fontSize="small" color="action" />
                      )}
                    </IconButton>
                  </>
                )}
              </Box>
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );

  const renderNonAccordionContent = () => (
    <Grid container spacing={1}>
      {Object.entries(relevantData).map(([key, value]) => {
        const displayValue = editedValues[key] ?? formatValue(key, value);
        const isEditing = editingField === key;

        return (
          <Grid item xs={12} key={key}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                bgcolor: 'white',
                p: 1.5,
                borderRadius: 1,
                '&:hover': { bgcolor: '#f9fafb' },
                minHeight: '72px',
              }}
            >
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="caption" color="text.secondary">
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </Typography>
                {isEditing ? (
                  <input
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '6px 8px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      fontFamily: 'Connect',
                      fontSize: '14px',
                      height: '32px',
                      marginTop: '4px',
                    }}
                  />
                ) : (
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    sx={{ mt: '4px' }}
                  >
                    {displayValue}
                  </Typography>
                )}
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.5,
                  ml: 1,
                  alignSelf: 'flex-end',
                }}
              >
                {isEditing ? (
                  <>
                    <IconButton
                      size="small"
                      onClick={() => saveEdit(key)}
                      sx={{
                        color: 'success.main',
                        width: 32,
                        height: 32,
                        '& svg': { fontSize: 18 },
                      }}
                    >
                      <Save />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={cancelEdit}
                      sx={{
                        color: 'error.main',
                        width: 32,
                        height: 32,
                        '& svg': { fontSize: 18 },
                      }}
                    >
                      <Close />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <IconButton
                      size="small"
                      onClick={() => startEditing(key, displayValue)}
                      sx={{
                        width: 32,
                        height: 32,
                        '& svg': { fontSize: 18 },
                      }}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(displayValue, key)}
                      sx={{
                        width: 32,
                        height: 32,
                        '& svg': { fontSize: 18 },
                      }}
                    >
                      {copiedField === key ? (
                        <CheckCircleOutline fontSize="small" color="success" />
                      ) : (
                        <FileCopy fontSize="small" color="action" />
                      )}
                    </IconButton>
                  </>
                )}
              </Box>
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );

  return (
    <Card
      sx={{
        width: '100%',
        mt: shouldUseAccordion ? 1 : 2,
        bgcolor: '#f9fafb',
        display: 'block',
        clear: 'both',
        fontFamily: 'Connect',
        ...(shouldUseAccordion && {
          border: '1.5px solid #e0e0e0',
          borderRadius: 2,
        }),
        ...(shouldUseAccordion ? { flex: 'initial' } : { flex: '1 1 100%' }),
      }}
      elevation={1}
    >
      {shouldUseAccordion ? (
        <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{
              bgcolor: 'white',
              borderBottom: expanded ? '1px solid rgba(0, 0, 0, 0.12)' : 'none',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ fontSize: 18 }}>
                <IconComponent fontSize="inherit" />
              </Box>
              <Typography
                variant="subtitle2"
                color="text.primary"
                sx={{ fontFamily: 'Connect' }}
              >
                {sectionConfig.title}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 2 }}>
            {renderAccordionContent()}
          </AccordionDetails>
        </Accordion>
      ) : (
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Box sx={{ fontSize: 20 }}>
              <IconComponent fontSize="inherit" />
            </Box>
            <Typography variant="subtitle2" color="text.primary">
              {sectionConfig.title}
            </Typography>
          </Box>
          {renderNonAccordionContent()}
        </CardContent>
      )}
    </Card>
  );
};

export default MLResultsDisplay;
